import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Megaphone,
  Plus,
  Edit,
  Trash2,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Calendar,
  DollarSign,
  Users,
  Filter,
  Send,
  Mail
} from 'lucide-react';
import {
  getCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  sendCampaign,
  getBusinessContacts,
  sendBulkEmail,
  createEmailCampaign,
  getEmailCampaigns,
  getEmailCampaign,
  sendFirstEmailToGroup,
  sendFollowUpEmailToGroup,
  getEmailTracking
} from '../../../redux/api';

const CampaignsView = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject: '',
    body: '',
    startDate: '',
    endDate: '',
    status: 'draft',
    targetAudience: '',
    budget: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [activeTab, setActiveTab] = useState('campaigns'); // 'campaigns', 'emails', or 'email-campaigns'
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [companiesWithEmail, setCompaniesWithEmail] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  
  // Email campaigns state
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [loadingEmailCampaigns, setLoadingEmailCampaigns] = useState(false);
  const [showEmailCampaignForm, setShowEmailCampaignForm] = useState(false);
  const [emailCampaignFormData, setEmailCampaignFormData] = useState({
    name: '',
    description: '',
    firstEmail: {
      subject: '',
      body: ''
    },
    followUpEmail: {
      subject: '',
      body: '',
      sendAfterDays: 7
    },
    hasFollowUp: false
  });
  const [submittingEmailCampaign, setSubmittingEmailCampaign] = useState(false);
  const [sendingFirstEmail, setSendingFirstEmail] = useState({});
  const [sendingFollowUp, setSendingFollowUp] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [emailTracking, setEmailTracking] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);

  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.result?.role === 'superadmin';

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Nemate pristup ovoj stranici');
      return;
    }
    fetchCampaigns(currentPage);
  }, [isSuperAdmin, currentPage]);

  // Fetch companies with email addresses when emails tab is active
  useEffect(() => {
    if (!isSuperAdmin) return;
    
    if (activeTab === 'emails') {
      fetchCompaniesWithEmail();
    } else if (activeTab === 'email-campaigns') {
      fetchEmailCampaigns();
    }
  }, [isSuperAdmin, activeTab]);

  const fetchCampaigns = async (page = 1) => {
    try {
      setLoading(true);
      const response = await getCampaigns(page, 10);
      const data = response.data || {};
      setCampaigns(data.campaigns || []);
      setTotalCampaigns(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(data.page || 1);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Greška pri učitavanju kampanja');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompaniesWithEmail = async () => {
    try {
      setLoadingCompanies(true);
      // Fetch all business contacts with email addresses (use large limit to get all)
      const response = await getBusinessContacts(1, 10000);
      const data = response.data || {};
      const allContacts = data.contacts || [];
      
      // Filter only contacts with email addresses
      const contactsWithEmail = allContacts.filter(contact => 
        contact.email && contact.email.trim() !== ''
      );
      
      setCompaniesWithEmail(contactsWithEmail);
    } catch (error) {
      console.error('Error fetching companies with email:', error);
      toast.error('Greška pri učitavanju kompanija sa email adresama');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      subject: '',
      body: '',
      startDate: '',
      endDate: '',
      status: 'draft',
      targetAudience: '',
      budget: ''
    });
    setEditingCampaign(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Naziv kampanje je obavezan');
      return;
    }

    setSubmitting(true);
    try {
      if (editingCampaign) {
        await updateCampaign({ id: editingCampaign._id, campaignData: formData });
        toast.success('Kampanja je uspešno ažurirana');
      } else {
        await createCampaign(formData);
        toast.success('Kampanja je uspešno kreirana');
      }
      resetForm();
      fetchCampaigns(currentPage);
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error(error.response?.data?.message || 'Greška pri čuvanju kampanje');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name || '',
      description: campaign.description || '',
      subject: campaign.subject || '',
      body: campaign.body || '',
      startDate: campaign.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : '',
      endDate: campaign.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : '',
      status: campaign.status || 'draft',
      targetAudience: campaign.targetAudience || '',
      budget: campaign.budget || ''
    });
    setShowForm(true);
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Da li ste sigurni da želite da pošaljete ovu kampanju svim klijentima sa email adresama?')) {
      return;
    }

    setSending(true);
    try {
      const response = await sendCampaign(campaignId);
      const data = response.data || {};
      toast.success(`Kampanja je uspešno poslata! Poslato: ${data.sentCount}, Neuspešno: ${data.failedCount}`);
      fetchCampaigns(currentPage);
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error(error.response?.data?.message || 'Greška pri slanju kampanje');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovu kampanju?')) {
      return;
    }

    try {
      await deleteCampaign(id);
      toast.success('Kampanja je uspešno obrisana');
      fetchCampaigns(currentPage);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Greška pri brisanju kampanje');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-500',
      active: 'bg-green-500',
      paused: 'bg-yellow-500',
      completed: 'bg-blue-500',
      cancelled: 'bg-red-500',
      sent: 'bg-purple-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Nacrt',
      active: 'Aktivna',
      paused: 'Pauzirana',
      completed: 'Završena',
      cancelled: 'Otkazana',
      sent: 'Poslata'
    };
    return labels[status] || status;
  };

  // Filter campaigns
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = !searchTerm || 
      campaign.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || campaign.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Get unique statuses for filter
  const uniqueStatuses = [...new Set(campaigns.map(c => c.status).filter(Boolean))].sort((a, b) => 
    a.localeCompare(b, 'sr', { sensitivity: 'base' })
  );

  if (!isSuperAdmin) {
    return null;
  }

  const handleBulkEmailSend = async () => {
    if (!emailSubject || !emailBody) {
      toast.error('Naslov i sadržaj emaila su obavezni');
      return;
    }

    if (companiesWithEmail.length === 0) {
      toast.error('Nema kompanija sa email adresama');
      return;
    }

    // Show confirmation with count
    const confirmed = window.confirm(
      `Da li ste sigurni da želite da pošaljete email svim ${companiesWithEmail.length} kompanijama koje imaju email adrese?\n\n` +
      `Naslov: ${emailSubject}\n\n` +
      `Ova operacija je besplatna.`
    );

    if (!confirmed) return;

    setSendingBulkEmail(true);
    try {
      const response = await sendBulkEmail({
        subject: emailSubject,
        body: emailBody
      });
      const data = response.data || {};
      toast.success(`Email je uspešno poslat! Poslato: ${data.sentCount}, Neuspešno: ${data.failedCount}`);
      setEmailSubject('');
      setEmailBody('');
      setShowBulkEmailModal(false);
    } catch (error) {
      console.error('Error sending bulk email:', error);
      toast.error(error.response?.data?.message || 'Greška pri slanju emailova');
    } finally {
      setSendingBulkEmail(false);
    }
  };

  // Email Campaign functions
  const fetchEmailCampaigns = async () => {
    try {
      setLoadingEmailCampaigns(true);
      const response = await getEmailCampaigns();
      setEmailCampaigns(response.data?.campaigns || []);
    } catch (error) {
      console.error('Error fetching email campaigns:', error);
      toast.error('Greška pri učitavanju email kampanja');
    } finally {
      setLoadingEmailCampaigns(false);
    }
  };

  const fetchEmailTracking = async (campaignId) => {
    try {
      setLoadingTracking(true);
      const response = await getEmailTracking(campaignId);
      setEmailTracking(response.data?.tracking || []);
    } catch (error) {
      console.error('Error fetching email tracking:', error);
      toast.error('Greška pri učitavanju tracking podataka');
    } finally {
      setLoadingTracking(false);
    }
  };

  const handleCreateEmailCampaign = async (e) => {
    e.preventDefault();
    
    if (!emailCampaignFormData.name || !emailCampaignFormData.firstEmail.subject || !emailCampaignFormData.firstEmail.body) {
      toast.error('Naziv kampanje, naslov i sadržaj prvog emaila su obavezni');
      return;
    }

    setSubmittingEmailCampaign(true);
    try {
      const campaignData = {
        name: emailCampaignFormData.name,
        description: emailCampaignFormData.description,
        firstEmail: {
          subject: emailCampaignFormData.firstEmail.subject,
          body: emailCampaignFormData.firstEmail.body
        }
      };

      if (emailCampaignFormData.hasFollowUp) {
        if (!emailCampaignFormData.followUpEmail.subject || !emailCampaignFormData.followUpEmail.body) {
          toast.error('Naslov i sadržaj follow-up emaila su obavezni ako je follow-up uključen');
          setSubmittingEmailCampaign(false);
          return;
        }
        campaignData.followUpEmail = {
          subject: emailCampaignFormData.followUpEmail.subject,
          body: emailCampaignFormData.followUpEmail.body,
          sendAfterDays: emailCampaignFormData.followUpEmail.sendAfterDays || 7
        };
      }

      await createEmailCampaign(campaignData);
      toast.success('Email kampanja je uspešno kreirana!');
      setShowEmailCampaignForm(false);
      setEmailCampaignFormData({
        name: '',
        description: '',
        firstEmail: { subject: '', body: '' },
        followUpEmail: { subject: '', body: '', sendAfterDays: 7 },
        hasFollowUp: false
      });
      fetchEmailCampaigns();
    } catch (error) {
      console.error('Error creating email campaign:', error);
      toast.error(error.response?.data?.message || 'Greška pri kreiranju email kampanje');
    } finally {
      setSubmittingEmailCampaign(false);
    }
  };

  const handleSendFirstEmail = async (campaignId, groupNumber) => {
    const confirmed = window.confirm(
      `Da li ste sigurni da želite da pošaljete prvi email Grupi ${groupNumber}?\n\n` +
      `Ovo će poslati email svim 100 email adresama u ovoj grupi.`
    );

    if (!confirmed) return;

    const key = `${campaignId}-${groupNumber}`;
    setSendingFirstEmail(prev => ({ ...prev, [key]: true }));
    
    try {
      await sendFirstEmailToGroup(campaignId, groupNumber);
      toast.success(`Prvi email je uspešno poslat Grupi ${groupNumber}!`);
      fetchEmailCampaigns();
    } catch (error) {
      console.error('Error sending first email:', error);
      toast.error(error.response?.data?.message || 'Greška pri slanju prvog emaila');
    } finally {
      setSendingFirstEmail(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSendFollowUp = async (campaignId, groupNumber) => {
    const confirmed = window.confirm(
      `Da li ste sigurni da želite da pošaljete follow-up email Grupi ${groupNumber}?\n\n` +
      `Ovo će poslati follow-up email svim 100 email adresama u ovoj grupi.`
    );

    if (!confirmed) return;

    const key = `${campaignId}-${groupNumber}`;
    setSendingFollowUp(prev => ({ ...prev, [key]: true }));
    
    try {
      await sendFollowUpEmailToGroup(campaignId, groupNumber);
      toast.success(`Follow-up email je uspešno poslat Grupi ${groupNumber}!`);
      fetchEmailCampaigns();
      if (selectedCampaign?._id === campaignId) {
        fetchEmailTracking(campaignId);
      }
    } catch (error) {
      console.error('Error sending follow-up email:', error);
      toast.error(error.response?.data?.message || 'Greška pri slanju follow-up emaila');
    } finally {
      setSendingFollowUp(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
          <Megaphone className="w-8 h-8" />
          Kampanje
        </h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-700">
          <button
            onClick={() => setActiveTab('campaigns')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'campaigns'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Kampanje
          </button>
          <button
            onClick={() => setActiveTab('emails')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'emails'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Mejlovi
          </button>
          <button
            onClick={() => setActiveTab('email-campaigns')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'email-campaigns'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Email Kampanje
          </button>
        </div>

        {/* Tab Content */}
        <div>
          {/* Email Tab Content */}
          {activeTab === 'emails' && (
            <div className="space-y-6">
              {/* Bulk Email Section */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Mail className="w-6 h-6" />
                  Masovno slanje mejlova
                </h2>
                
                {loadingCompanies ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : (
                  <div>
                    <div className="mb-4 p-4 bg-gray-700 rounded-lg">
                      <p className="text-gray-300">
                        <span className="font-semibold text-white">{companiesWithEmail.length}</span> kompanija ima email adrese i može primiti mejl.
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        Slanje mejlova je besplatno. Mejlovi će biti poslati svim kompanijama koje imaju email adrese u sekciji "Poslovanje".
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Naslov emaila (Subject) *</label>
                        <input
                          type="text"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          placeholder="Unesite naslov emaila"
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Sadržaj emaila (Body) *</label>
                        <textarea
                          value={emailBody}
                          onChange={(e) => setEmailBody(e.target.value)}
                          rows={10}
                          placeholder="Unesite sadržaj emaila (možete koristiti HTML)"
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        />
                        <p className="text-xs text-gray-400 mt-1">Možete koristiti HTML formatiranje</p>
                      </div>

                      <button
                        onClick={handleBulkEmailSend}
                        disabled={sendingBulkEmail || !emailSubject || !emailBody || companiesWithEmail.length === 0}
                        className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
                      >
                        {sendingBulkEmail ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Šalje se...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            Pošalji mejl svim {companiesWithEmail.length} kompanijama
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Campaigns Tab Content */}
          {activeTab === 'campaigns' && (
            <div>
              {/* Top Section: Stats, Search, Filters, Actions */}
              <div className="bg-gray-800 rounded-lg p-4 mb-6 flex flex-wrap items-center gap-4">
                {/* Statistics */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Ukupno kampanja:</span>
                  <span className="font-bold text-white">{totalCampaigns}</span>
                  {filterStatus && (
                    <span className="text-gray-400">
                      | Filtrirano: <span className="font-bold text-white">{filteredCampaigns.length}</span>
                    </span>
                  )}
                </div>

                {/* Search */}
                <input
                  type="text"
                  placeholder="Pretraži kampanje..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-w-[200px]"
                />

                {/* Filters */}
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px]"
                >
                  <option value="">Svi statusi</option>
                  {uniqueStatuses.map(status => (
                    <option key={status} value={status}>{getStatusLabel(status)}</option>
                  ))}
                </select>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      resetForm();
                      setShowForm(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Nova kampanja
                  </button>
                  {(filterStatus || searchTerm) && (
                    <button
                      onClick={() => {
                        setFilterStatus('');
                        setSearchTerm('');
                      }}
                      className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Obriši filtere
                    </button>
                  )}
                </div>
              </div>

              {/* Form Modal */}
              {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold">
                        {editingCampaign ? 'Izmeni kampanju' : 'Nova kampanja'}
                      </h2>
                      <button
                        onClick={resetForm}
                        className="text-gray-400 hover:text-white"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Naziv kampanje *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Opis</label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Email naslov (Subject) *</label>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          placeholder="Unesite naslov emaila"
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Email sadržaj (Body) *</label>
                        <textarea
                          name="body"
                          value={formData.body}
                          onChange={handleInputChange}
                          required
                          rows={8}
                          placeholder="Unesite sadržaj emaila (možete koristiti HTML)"
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        />
                        <p className="text-xs text-gray-400 mt-1">Možete koristiti HTML formatiranje</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Datum početka</label>
                          <input
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Datum završetka</label>
                          <input
                            type="date"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleInputChange}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Status</label>
                          <select
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="draft">Nacrt</option>
                            <option value="active">Aktivna</option>
                            <option value="paused">Pauzirana</option>
                            <option value="completed">Završena</option>
                            <option value="cancelled">Otkazana</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Budžet</label>
                          <input
                            type="number"
                            name="budget"
                            value={formData.budget}
                            onChange={handleInputChange}
                            step="0.01"
                            min="0"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Ciljna grupa</label>
                        <input
                          type="text"
                          name="targetAudience"
                          value={formData.targetAudience}
                          onChange={handleInputChange}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={resetForm}
                          className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
                        >
                          Otkaži
                        </button>
                        <button
                          type="submit"
                          disabled={submitting}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {submitting ? (
                            <span className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Čuvanje...
                            </span>
                          ) : (
                            editingCampaign ? 'Sačuvaj izmene' : 'Kreiraj kampanju'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
            )}

            {/* Campaigns List */}
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                {searchTerm || filterStatus ? 'Nema kampanja koje odgovaraju filterima' : 'Nema kampanja. Kreirajte novu kampanju.'}
              </div>
            ) : (
              <div>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full bg-gray-800 rounded-lg overflow-hidden">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Naziv</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Datum početka</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Datum završetka</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Budžet</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Ciljna grupa</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Akcije</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {filteredCampaigns.map((campaign) => (
                        <tr key={campaign._id} className="hover:bg-gray-750">
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium">{campaign.name}</div>
                            {campaign.subject && (
                              <div className="text-xs text-gray-300 mt-1 font-semibold">{campaign.subject}</div>
                            )}
                            {campaign.description && (
                              <div className="text-xs text-gray-400 mt-1 line-clamp-1">{campaign.description}</div>
                            )}
                            {campaign.status === 'sent' && (
                              <div className="text-xs text-green-400 mt-1">
                                Poslato: {campaign.sentCount || 0} | Neuspešno: {campaign.failedCount || 0}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                              {getStatusLabel(campaign.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {campaign.startDate ? new Date(campaign.startDate).toLocaleDateString('sr-RS') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {campaign.endDate ? new Date(campaign.endDate).toLocaleDateString('sr-RS') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {campaign.budget ? `${campaign.budget.toLocaleString('sr-RS')} RSD` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {campaign.targetAudience || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              {campaign.status !== 'sent' && campaign.subject && campaign.body && (
                                <button
                                  onClick={() => handleSendCampaign(campaign._id)}
                                  disabled={sending}
                                  className="text-green-400 hover:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Pošalji kampanju"
                                >
                                  <Send className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleEdit(campaign)}
                                className="text-blue-400 hover:text-blue-300"
                                title="Izmeni"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(campaign._id)}
                                className="text-red-400 hover:text-red-300"
                                title="Obriši"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {filteredCampaigns.map((campaign) => (
                    <div key={campaign._id} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-lg">{campaign.name}</h3>
                        <div className="flex gap-2">
                          {campaign.status !== 'sent' && campaign.subject && campaign.body && (
                            <button
                              onClick={() => handleSendCampaign(campaign._id)}
                              disabled={sending}
                              className="text-green-400 hover:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Pošalji kampanju"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(campaign)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(campaign._id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {campaign.subject && (
                        <p className="text-sm text-gray-300 font-semibold mb-2">{campaign.subject}</p>
                      )}
                      {campaign.description && (
                        <p className="text-sm text-gray-400 mb-3">{campaign.description}</p>
                      )}
                      {campaign.status === 'sent' && (
                        <div className="text-xs text-green-400 mb-2">
                          Poslato: {campaign.sentCount || 0} | Neuspešno: {campaign.failedCount || 0}
                        </div>
                      )}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                            {getStatusLabel(campaign.status)}
                          </span>
                        </div>
                        {campaign.startDate && (
                          <div className="flex items-center gap-2 text-gray-300">
                            <Calendar className="w-4 h-4" />
                            <span>Početak: {new Date(campaign.startDate).toLocaleDateString('sr-RS')}</span>
                          </div>
                        )}
                        {campaign.endDate && (
                          <div className="flex items-center gap-2 text-gray-300">
                            <Calendar className="w-4 h-4" />
                            <span>Kraj: {new Date(campaign.endDate).toLocaleDateString('sr-RS')}</span>
                          </div>
                        )}
                        {campaign.budget && (
                          <div className="flex items-center gap-2 text-gray-300">
                            <DollarSign className="w-4 h-4" />
                            <span>{campaign.budget.toLocaleString('sr-RS')} RSD</span>
                          </div>
                        )}
                        {campaign.targetAudience && (
                          <div className="flex items-center gap-2 text-gray-300">
                            <Users className="w-4 h-4" />
                            <span>{campaign.targetAudience}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex justify-center items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Prethodna
                    </button>
                    <span className="text-gray-300">
                      Strana {currentPage} od {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      Sledeća
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
            </div>
          )}

          {/* Email Campaigns Tab Content */}
          {activeTab === 'email-campaigns' && (
            <div className="space-y-6">
              {/* Header with Create Button */}
              <div className="bg-gray-800 rounded-lg p-4 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Mail className="w-6 h-6" />
                    Email Kampanje sa Grupama
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Kreirajte kampanje sa grupama od po 100 email adresa. Prvo pošaljite prvi email, pa posle 7 dana follow-up.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEmailCampaignFormData({
                      name: '',
                      description: '',
                      firstEmail: { subject: '', body: '' },
                      followUpEmail: { subject: '', body: '', sendAfterDays: 7 },
                      hasFollowUp: false
                    });
                    setShowEmailCampaignForm(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Nova Email Kampanja
                </button>
              </div>

              {/* Email Campaigns List */}
              {loadingEmailCampaigns ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : emailCampaigns.length === 0 ? (
                <div className="text-center py-12 text-gray-400 bg-gray-800 rounded-lg">
                  Nema email kampanja. Kreirajte novu kampanju.
                </div>
              ) : (
                <div className="space-y-4">
                  {emailCampaigns.map((campaign) => (
                    <div key={campaign._id} className="bg-gray-800 rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-bold mb-2">{campaign.name}</h3>
                          {campaign.description && (
                            <p className="text-gray-400 text-sm mb-2">{campaign.description}</p>
                          )}
                          <div className="flex gap-4 text-sm text-gray-300">
                            <span>Ukupno primaoca: <strong className="text-white">{campaign.totalRecipients || 0}</strong></span>
                            <span>Grupa: <strong className="text-white">{campaign.groups?.length || 0}</strong></span>
                            <span>Prvi email poslat: <strong className="text-white">{campaign.firstEmailSentCount || 0}</strong></span>
                            {campaign.followUpEmail && (
                              <span>Follow-up poslat: <strong className="text-white">{campaign.followUpEmailSentCount || 0}</strong></span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (selectedCampaign?._id === campaign._id) {
                              setSelectedCampaign(null);
                              setEmailTracking(null);
                            } else {
                              setSelectedCampaign(campaign);
                              fetchEmailTracking(campaign._id);
                            }
                          }}
                          className="text-blue-400 hover:text-blue-300 text-sm"
                        >
                          {selectedCampaign?._id === campaign._id ? 'Sakrij detalje' : 'Prikaži detalje'}
                        </button>
                      </div>

                      {/* Groups */}
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-gray-300 mb-2">Grupe:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {campaign.groups?.map((group) => {
                            const getGroupStatusColor = (status) => {
                              if (status === 'completed') return 'bg-green-600';
                              if (status === 'followup_sent') return 'bg-blue-600';
                              if (status === 'first_sent') return 'bg-yellow-600';
                              return 'bg-gray-600';
                            };

                            const getGroupStatusLabel = (status) => {
                              if (status === 'completed') return 'Završeno';
                              if (status === 'followup_sent') return 'Follow-up poslat';
                              if (status === 'first_sent') return 'Prvi email poslat';
                              return 'Čeka';
                            };

                            const canSendFollowUp = group.status === 'first_sent' && 
                              group.firstEmailSentAt && 
                              campaign.followUpEmail &&
                              new Date() - new Date(group.firstEmailSentAt) >= (campaign.followUpEmail.sendAfterDays || 7) * 24 * 60 * 60 * 1000;

                            return (
                              <div key={group.groupNumber} className="bg-gray-700 rounded-lg p-4">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="font-semibold">Grupa {group.groupNumber}</span>
                                  <span className={`px-2 py-1 rounded text-xs ${getGroupStatusColor(group.status)}`}>
                                    {getGroupStatusLabel(group.status)}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-400 mb-3">
                                  {group.emailAddresses?.length || 0} email adresa
                                </div>
                                <div className="flex gap-2">
                                  {group.status === 'pending' && (
                                    <button
                                      onClick={() => handleSendFirstEmail(campaign._id, group.groupNumber)}
                                      disabled={sendingFirstEmail[`${campaign._id}-${group.groupNumber}`]}
                                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                                    >
                                      {sendingFirstEmail[`${campaign._id}-${group.groupNumber}`] ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Send className="w-4 h-4" />
                                      )}
                                      Prvi Email
                                    </button>
                                  )}
                                  {canSendFollowUp && (
                                    <button
                                      onClick={() => handleSendFollowUp(campaign._id, group.groupNumber)}
                                      disabled={sendingFollowUp[`${campaign._id}-${group.groupNumber}`]}
                                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                                    >
                                      {sendingFollowUp[`${campaign._id}-${group.groupNumber}`] ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Send className="w-4 h-4" />
                                      )}
                                      Follow-up
                                    </button>
                                  )}
                                  {group.status === 'first_sent' && !canSendFollowUp && campaign.followUpEmail && (
                                    <div className="flex-1 text-xs text-gray-400 text-center py-2">
                                      {group.firstEmailSentAt ? (
                                        <>
                                          Čeka {campaign.followUpEmail.sendAfterDays || 7} dana
                                          <br />
                                          ({Math.floor((new Date() - new Date(group.firstEmailSentAt)) / (1000 * 60 * 60 * 24))} dana prošlo)
                                        </>
                                      ) : 'Čeka'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Tracking Details */}
                      {selectedCampaign?._id === campaign._id && (
                        <div className="mt-4 bg-gray-700 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-gray-300 mb-2">Tracking podaci:</h4>
                          {loadingTracking ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                            </div>
                          ) : emailTracking && emailTracking.length > 0 ? (
                            <div className="max-h-60 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-gray-600">
                                    <th className="text-left py-2 px-2">Email</th>
                                    <th className="text-left py-2 px-2">Tip</th>
                                    <th className="text-left py-2 px-2">Status</th>
                                    <th className="text-left py-2 px-2">Datum</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {emailTracking.slice(0, 50).map((track, idx) => (
                                    <tr key={idx} className="border-b border-gray-600">
                                      <td className="py-2 px-2">{track.email}</td>
                                      <td className="py-2 px-2">
                                        <span className={`px-2 py-1 rounded ${track.emailType === 'first' ? 'bg-blue-600' : 'bg-purple-600'}`}>
                                          {track.emailType === 'first' ? 'Prvi' : 'Follow-up'}
                                        </span>
                                      </td>
                                      <td className="py-2 px-2">
                                        <span className={`px-2 py-1 rounded ${track.status === 'sent' ? 'bg-green-600' : 'bg-red-600'}`}>
                                          {track.status === 'sent' ? 'Poslat' : 'Neuspešno'}
                                        </span>
                                      </td>
                                      <td className="py-2 px-2 text-gray-400">
                                        {new Date(track.sentAt).toLocaleString('sr-RS')}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {emailTracking.length > 50 && (
                                <p className="text-xs text-gray-400 mt-2">Prikazano prvih 50 od {emailTracking.length} zapisa</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-gray-400 text-sm">Nema tracking podataka</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Email Campaign Form Modal */}
              {showEmailCampaignForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold">Nova Email Kampanja</h2>
                      <button
                        onClick={() => setShowEmailCampaignForm(false)}
                        className="text-gray-400 hover:text-white"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>

                    <form onSubmit={handleCreateEmailCampaign} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Naziv kampanje *</label>
                        <input
                          type="text"
                          value={emailCampaignFormData.name}
                          onChange={(e) => setEmailCampaignFormData({ ...emailCampaignFormData, name: e.target.value })}
                          required
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Opis</label>
                        <textarea
                          value={emailCampaignFormData.description}
                          onChange={(e) => setEmailCampaignFormData({ ...emailCampaignFormData, description: e.target.value })}
                          rows={3}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="border-t border-gray-700 pt-4">
                        <h3 className="text-lg font-semibold mb-3">Prvi Email (Faza 1) *</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Naslov (Subject) *</label>
                            <input
                              type="text"
                              value={emailCampaignFormData.firstEmail.subject}
                              onChange={(e) => setEmailCampaignFormData({
                                ...emailCampaignFormData,
                                firstEmail: { ...emailCampaignFormData.firstEmail, subject: e.target.value }
                              })}
                              required
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Sadržaj (Body) *</label>
                            <textarea
                              value={emailCampaignFormData.firstEmail.body}
                              onChange={(e) => setEmailCampaignFormData({
                                ...emailCampaignFormData,
                                firstEmail: { ...emailCampaignFormData.firstEmail, body: e.target.value }
                              })}
                              required
                              rows={8}
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                            />
                            <p className="text-xs text-gray-400 mt-1">Možete koristiti HTML formatiranje</p>
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-gray-700 pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <input
                            type="checkbox"
                            id="hasFollowUp"
                            checked={emailCampaignFormData.hasFollowUp}
                            onChange={(e) => setEmailCampaignFormData({ ...emailCampaignFormData, hasFollowUp: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <label htmlFor="hasFollowUp" className="text-sm font-medium">Dodaj Follow-up Email (Faza 2)</label>
                        </div>

                        {emailCampaignFormData.hasFollowUp && (
                          <div className="space-y-4 pl-6 border-l-2 border-gray-600">
                            <div>
                              <label className="block text-sm font-medium mb-2">Naslov (Subject) *</label>
                              <input
                                type="text"
                                value={emailCampaignFormData.followUpEmail.subject}
                                onChange={(e) => setEmailCampaignFormData({
                                  ...emailCampaignFormData,
                                  followUpEmail: { ...emailCampaignFormData.followUpEmail, subject: e.target.value }
                                })}
                                required={emailCampaignFormData.hasFollowUp}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Sadržaj (Body) *</label>
                              <textarea
                                value={emailCampaignFormData.followUpEmail.body}
                                onChange={(e) => setEmailCampaignFormData({
                                  ...emailCampaignFormData,
                                  followUpEmail: { ...emailCampaignFormData.followUpEmail, body: e.target.value }
                                })}
                                required={emailCampaignFormData.hasFollowUp}
                                rows={8}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                              />
                              <p className="text-xs text-gray-400 mt-1">Možete koristiti HTML formatiranje</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Pošalji posle (dana) *</label>
                              <input
                                type="number"
                                min="1"
                                value={emailCampaignFormData.followUpEmail.sendAfterDays}
                                onChange={(e) => setEmailCampaignFormData({
                                  ...emailCampaignFormData,
                                  followUpEmail: { ...emailCampaignFormData.followUpEmail, sendAfterDays: parseInt(e.target.value) || 7 }
                                })}
                                required={emailCampaignFormData.hasFollowUp}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="text-xs text-gray-400 mt-1">Default: 7 dana</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
                        <button
                          type="button"
                          onClick={() => setShowEmailCampaignForm(false)}
                          className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
                        >
                          Otkaži
                        </button>
                        <button
                          type="submit"
                          disabled={submittingEmailCampaign}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {submittingEmailCampaign ? (
                            <span className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Kreiranje...
                            </span>
                          ) : (
                            'Kreiraj Kampanju'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default CampaignsView;

